// src/components/admin/AdminLekhiPlansNew.js
// READ-ONLY view of the new 60-day Lekhi schedule + content readiness per level.
// Laptop layout: one row = a study day + its revision day (same topics).
import React, { useState, useEffect, useCallback } from 'react';
import {
  LEKHI_LEVELS,
  LEKHI_PLAN_DAYS,
  getLekhiPlanForDay,
  getLekhiTopicContentKey,
  getLekhiTopicContentIndex,
  getLekhiTopicNumber
} from '../../services/firebase/firestoreService';
import { getSubjectName, getTopicNameMr } from '../../services/lekhiTopicNames';

const LEVEL_SHORT = { easy: 'E', medium: 'M', hard: 'H' };
const FILE_TYPES = ['studyText', 'revisionText', 'cheatSheet', 'mcqs', 'audio1', 'audio2'];
const FILE_LABEL = {
  studyText: 'StudyText', revisionText: 'RevisionText', cheatSheet: 'CheatSheet',
  mcqs: 'MCQs', audio1: 'Audio 1', audio2: 'Audio 2'
};
// What each day type needs, per level
const NEEDS = {
  study: ['studyText', 'mcqs', 'audio1', 'audio2'],
  revision: ['revisionText', 'cheatSheet', 'mcqs']
};
const BLOCK_COUNT = LEKHI_PLAN_DAYS / 20; // 3 blocks of 20 days

const missingFiles = (index, dayType, subject, topic, level) => {
  const item = index[getLekhiTopicContentKey(subject, topic, level)] || {};
  return NEEDS[dayType].filter(f => !item[f]);
};

const thStyle = {
  padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #ddd',
  background: '#f8f9fa', whiteSpace: 'nowrap', fontSize: '14px'
};
const tdStyle = { padding: '7px 10px', borderBottom: '1px solid #f1f3f5', whiteSpace: 'nowrap', verticalAlign: 'middle', fontSize: '15px' };
const statBox = { background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '6px 10px' };

export default function AdminLekhiPlansNew() {
  const [index, setIndex] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setIndex(await getLekhiTopicContentIndex());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---------- Summary ----------
  const allDays = Array.from({ length: LEKHI_PLAN_DAYS }, (_, i) => getLekhiPlanForDay(i + 1));
  const isDayReady = (p) => p.topics.every(tp =>
    LEKHI_LEVELS.every(l => missingFiles(index, p.dayType, tp.subject, tp.topic, l).length === 0)
  );
  const readyDays = allDays.filter(isDayReady).length;

  // Unique topics used in the plan → count missing files per type across all topic-levels
  const uniqueTopics = [];
  const seen = new Set();
  allDays.forEach(p => p.topics.forEach(tp => {
    const k = `${tp.subject}|${tp.topic}`;
    if (!seen.has(k)) { seen.add(k); uniqueTopics.push(tp); }
  }));
  const missingByType = {};
  FILE_TYPES.forEach(f => { missingByType[f] = 0; });
  uniqueTopics.forEach(tp => {
    LEKHI_LEVELS.forEach(l => {
      const item = index[getLekhiTopicContentKey(tp.subject, tp.topic, l)] || {};
      FILE_TYPES.forEach(f => { if (!item[f]) missingByType[f]++; });
    });
  });
  const totalSlots = uniqueTopics.length * LEKHI_LEVELS.length;

  // ---------- Pieces ----------
  const renderMarks = (dayType, tp) => (
    <span style={{ display: 'inline-flex', gap: '3px' }}>
      {LEKHI_LEVELS.map(l => {
        const missing = missingFiles(index, dayType, tp.subject, tp.topic, l);
        const ok = missing.length === 0;
        return (
          <span key={l}
            title={ok ? `${l}: ready` : `${l} missing: ${missing.map(f => FILE_LABEL[f]).join(', ')}`}
            style={{
              display: 'inline-block', minWidth: '22px', textAlign: 'center', fontSize: '13px',
              fontWeight: 700, padding: '2px 5px', borderRadius: '4px', cursor: 'default',
              background: ok ? '#dcfce7' : '#fee2e2', color: ok ? '#15803d' : '#b91c1c'
            }}>
            {LEVEL_SHORT[l]}
          </span>
        );
      })}
    </span>
  );

  const renderTopicName = (tp) => (
    <span>
      <span style={{ color: '#888', fontSize: '14px' }}>{getSubjectName(tp.subject, 'en')} · </span>
      <span style={{ fontWeight: 600 }}>{getLekhiTopicNumber(tp.subject, tp.topic)}. {getTopicNameMr(tp.topic)}</span>
    </span>
  );

  const dayBadge = (day, ready) => (
    <span style={{
      display: 'inline-block', minWidth: '24px', textAlign: 'center', fontWeight: 700,
      padding: '2px 7px', borderRadius: '6px', fontSize: '14px',
      background: ready ? '#dcfce7' : '#f1f3f5', color: ready ? '#15803d' : '#555'
    }}>
      {day}
    </span>
  );

  const typeHeader = (label, color) => (
    <span style={{ color, fontWeight: 700 }}>{label}</span>
  );

  // ---------- Rows: one per study day, paired with its revision day ----------
  const blocks = Array.from({ length: BLOCK_COUNT }, (_, b) => {
    const pairs = [];
    for (let slot = 0; slot < 10; slot++) {
      const studyDay = b * 20 + slot + 1;
      pairs.push({ study: allDays[studyDay - 1], revision: allDays[studyDay + 10 - 1] });
    }
    return { b, from: b * 20 + 1, to: b * 20 + 20, pairs };
  });

  return (
    <div>
      <div className="admin-section-title">Lekhi Plan (New) — read only</div>

      {/* ===== SUMMARY ===== */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ ...statBox, background: '#eef0ff', borderColor: '#c7cbf7' }}>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#4f51d4' }}>
              {loading ? '...' : `${readyDays} / ${LEKHI_PLAN_DAYS}`}
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>Days fully ready</div>
          </div>
          {FILE_TYPES.map(f => (
            <div key={f} style={statBox}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: missingByType[f] ? '#b91c1c' : '#15803d' }}>
                {loading ? '...' : missingByType[f]}
              </div>
              <div style={{ fontSize: '13px', color: '#888' }}>{FILE_LABEL[f]} missing / {totalSlots}</div>
            </div>
          ))}
          <div style={{ flex: 1 }} />
          <button className="admin-btn admin-btn-secondary" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <p style={{ fontSize: '13px', color: '#888', margin: '10px 0 0' }}>
          Each row = a study day and its revision day (same topics). Odd days: Marathi Grammar + Maths · Even days: Reasoning + GK.
          E/M/H green = that level has everything the day needs (study: StudyText, MCQs, Audio 1, Audio 2 · revision: RevisionText, CheatSheet, MCQs).
          Hover a red mark to see what is missing.
        </p>
        {error && <div className="admin-status-msg error">Error: {error}</div>}
      </div>

      {/* ===== SCHEDULE ===== */}
      <div className="admin-card">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Days</th>
                <th style={thStyle}>Topic 1</th>
                <th style={thStyle}>{typeHeader('अभ्यास', '#4f51d4')}</th>
                <th style={thStyle}>{typeHeader('उजळणी', '#c2410c')}</th>
                <th style={{ ...thStyle, borderLeft: '2px solid #e9ecef' }}>Topic 2</th>
                <th style={thStyle}>{typeHeader('अभ्यास', '#4f51d4')}</th>
                <th style={thStyle}>{typeHeader('उजळणी', '#c2410c')}</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map(blk => (
                <React.Fragment key={blk.b}>
                  <tr>
                    <td colSpan={7} style={{
                      padding: '10px 8px', background: '#f8f9fa', fontWeight: 700, fontSize: '14px',
                      color: '#444', borderBottom: '1px solid #ddd'
                    }}>
                      Block {blk.b + 1} · Days {blk.from}–{blk.to}
                    </td>
                  </tr>
                  {blk.pairs.map(({ study, revision }) => {
                    const t1 = study.topics[0];
                    const t2 = study.topics[1];
                    return (
                      <tr key={study.day}>
                        <td style={tdStyle}>
                          {dayBadge(study.day, isDayReady(study))}
                          <span style={{ color: '#bbb', margin: '0 4px' }}>·</span>
                          {dayBadge(revision.day, isDayReady(revision))}
                        </td>
                        <td style={tdStyle}>{t1 ? renderTopicName(t1) : '—'}</td>
                        <td style={tdStyle}>{t1 ? renderMarks('study', t1) : '—'}</td>
                        <td style={tdStyle}>{t1 ? renderMarks('revision', t1) : '—'}</td>
                        {t2 ? (
                          <>
                            <td style={{ ...tdStyle, borderLeft: '2px solid #e9ecef' }}>{renderTopicName(t2)}</td>
                            <td style={tdStyle}>{renderMarks('study', t2)}</td>
                            <td style={tdStyle}>{renderMarks('revision', t2)}</td>
                          </>
                        ) : (
                          <td colSpan={3} style={{ ...tdStyle, borderLeft: '2px solid #e9ecef', color: '#888' }}>
                            — Reasoning only
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}